import { useEffect, useState, useRef } from "react";
import { useIntl } from "react-intl";
import PlusIcon from "../../../../../assets/icons/PlusIcon";
import Spinner from "../../../../../assets/icons/Spinner";
import Message from "../../../../../i18n/Message";
import label from "../../../../../i18n/label";
import backend from "../../../../../backend/backend";
import Notify from "../../../../shared/Notify";
import WebpageAssetItem from "./WebpageAssetItem";
import CreateWebpageDialog from "./CreateWebpageDialog";

import "./WebpagesContentPanel.scss";

const WebpagesContentPanel = ({ kbase, workspaceId }) => {
  const intl = useIntl();
  const [assets, setAssets] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [listCounter, setListCounter] = useState(0);
  const pollingTimerRef = useRef(null);

  useEffect(() => {
    if (kbase && workspaceId) {
      setLoading(true);
      const assetTypes = ["URL"]; // Only show URL type assets
      backend
        .getKBAssets(workspaceId, kbase.uid, null, null, assetTypes)
        .then((data) => {
          setAssets(data);
          setLoading(false);
        })
        .catch((err) => {
          console.error("Failed to load webpage assets", err);
          Notify.error(label(intl, "error_load"));
          setLoading(false);
        });
    }
  }, [kbase, workspaceId, listCounter, intl]);

  // Polling effect for pending/in-progress assets
  useEffect(() => {
    // Clear any existing timer
    if (pollingTimerRef.current) {
      clearTimeout(pollingTimerRef.current);
      pollingTimerRef.current = null;
    }

    // Check if there are any pending or in-progress assets (RAG or crawling)
    const hasPendingAssets = assets?.some(
      (asset) =>
        asset.ragStatus === "PENDING" ||
        asset.ragStatus === "IN_PROGRESS" ||
        asset.crawlingStatus === "IN_PROGRESS"
    );

    if (hasPendingAssets && kbase && workspaceId) {
      // Schedule next poll in 5 seconds
      pollingTimerRef.current = setTimeout(() => {
        const assetTypes = ["URL"];
        backend
          .getKBAssets(workspaceId, kbase.uid, null, null, assetTypes)
          .then((data) => {
            setAssets(data);
          })
          .catch((err) => {
            console.error("Failed to poll webpage assets", err);
          });
      }, 5000);
    }

    // Cleanup on unmount
    return () => {
      if (pollingTimerRef.current) {
        clearTimeout(pollingTimerRef.current);
        pollingTimerRef.current = null;
      }
    };
  }, [assets, kbase, workspaceId]);

  const handleDelete = (asset) => {
    backend
      .deleteKBAsset(workspaceId, kbase.uid, asset.uid)
      .then(() => {
        Notify.success(label(intl, "delete_success"));
        setListCounter((prev) => prev + 1);
      })
      .catch((err) => {
        console.error("Failed to delete webpage asset", err);
        Notify.error(label(intl, "error_delete"));
      });
  };

  const handleUpdate = (asset, data) => {
    backend
      .updateKBAsset(workspaceId, kbase.uid, asset.uid, data)
      .then(() => {
        Notify.success(label(intl, "update_success"));
        setListCounter((prev) => prev + 1);
      })
      .catch((err) => {
        console.error("Failed to update webpage asset", err);
        Notify.error(label(intl, "error_update"));
      });
  };

  const onWebpageCreated = () => {
    setListCounter((prev) => prev + 1);
  };

  return (
    <div className="webpages_content_panel">
      <div className="actions_panel">
        <button
          className="btn btn-outline-dark"
          onClick={() => setShowCreateDialog(true)}
        >
          <PlusIcon size="18" color="#344767" />
          <Message id="add_webpage" />
        </button>
      </div>

      <div className="webpages_list">
        {loading && <Spinner />}
        {!loading && assets && assets.length === 0 && (
          <div className="empty_state">
            <Message id="no_webpages_yet" />
          </div>
        )}
        {!loading &&
          assets &&
          assets.map((asset) => (
            <WebpageAssetItem
              key={asset.uid}
              asset={asset}
              onDelete={handleDelete}
              onUpdate={handleUpdate}
            />
          ))}
      </div>

      {showCreateDialog && (
        <CreateWebpageDialog
          workspaceId={workspaceId}
          kbaseId={kbase.uid}
          onClose={() => setShowCreateDialog(false)}
          onSuccess={onWebpageCreated}
        />
      )}
    </div>
  );
};

export default WebpagesContentPanel;
